/**
 * Theme registry.
 *
 * Adding a preset = one entry here + one difference block in
 * `src/assets/styles/tokens.css`. Nothing else in the app hardcodes theme ids.
 *
 * Convention (relied on by the anti-flash script in `index.html`, which cannot
 * import this module): an id ending in `-dark` is a dark-scheme theme.
 */

export type ColorScheme = 'light' | 'dark'

/** Stored preference. `'system'` follows `prefers-color-scheme`. */
export type ThemeMode = string

export interface ThemeDef {
  id: string
  /** Formal system noun — no marketing copy. */
  label: string
  scheme: ColorScheme
  /**
   * Swatch preview: [background, primary]. `bg` also drives
   * `<meta name="theme-color">` (mobile browser chrome).
   *
   * Literal values, not tokens: the picker renders every theme's swatch at once
   * while only one theme is applied to `:root`, so `var(--color-*)` would make
   * all swatches identical.
   */
  preview: [string, string]
  /** False for the runtime-registered custom theme. */
  builtin: boolean
}

export const SYSTEM_MODE = 'system'
/** Two ids so the `-dark` suffix convention also resolves custom themes. */
export const CUSTOM_LIGHT_ID = 'custom'
export const CUSTOM_DARK_ID = 'custom-dark'
export const FALLBACK_THEME = 'light'

export function isCustomTheme(id: string): boolean {
  return id === CUSTOM_LIGHT_ID || id === CUSTOM_DARK_ID
}

/**
 * Seeds for the custom theme. Everything else is derived in CSS — see the
 * `[data-theme='custom']` blocks in `tokens.css`.
 */
export interface ThemeSeeds {
  /** 0-360. */
  hue: number
  /** 0-1. 0 collapses to the hue-less neutral ramp. */
  chroma: number
}

export const DEFAULT_SEEDS: ThemeSeeds = { hue: 220, chroma: 0.6 }

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n
}

/** Coerces anything (localStorage JSON, API payload, form input) into range. */
export function normalizeSeeds(input: unknown): ThemeSeeds {
  const src = (input ?? {}) as Partial<Record<keyof ThemeSeeds, unknown>>
  const hue = Number(src.hue)
  const chroma = Number(src.chroma)
  return {
    hue: Number.isFinite(hue) ? Math.round(clamp(hue, 0, 360)) % 360 : DEFAULT_SEEDS.hue,
    chroma: Number.isFinite(chroma)
      ? Math.round(clamp(chroma, 0, 1) * 100) / 100
      : DEFAULT_SEEDS.chroma,
  }
}

export function seedsEqual(a: ThemeSeeds, b: ThemeSeeds): boolean {
  return a.hue === b.hue && a.chroma === b.chroma
}

/** Rejects anything that must not reach `setAttribute('data-theme', …)`. */
const ID_SHAPE = /^[a-z0-9_-]{1,32}$/

export const THEMES: ThemeDef[] = [
  { id: 'light', label: '浅色', scheme: 'light', preview: ['#fafafa', '#111111'], builtin: true },
  { id: 'dark', label: '深色', scheme: 'dark', preview: ['#0a0a0a', '#f5f5f5'], builtin: true },
  { id: 'slate', label: '石墨', scheme: 'light', preview: ['#f8f9fb', '#1f2937'], builtin: true },
  {
    id: 'slate-dark',
    label: '石墨深色',
    scheme: 'dark',
    preview: ['#0b0e14', '#e8edf5'],
    builtin: true,
  },
  { id: 'sand', label: '沙岩', scheme: 'light', preview: ['#faf8f5', '#2b2419'], builtin: true },
  {
    id: 'sand-dark',
    label: '沙岩深色',
    scheme: 'dark',
    preview: ['#0d0b09', '#f0ebe0'],
    builtin: true,
  },
]

export function isValidThemeId(id: unknown): id is string {
  return typeof id === 'string' && ID_SHAPE.test(id)
}

export function findTheme(id: string): ThemeDef | null {
  return THEMES.find((t) => t.id === id) ?? null
}

/**
 * Scheme of a theme id without consulting the registry — the `-dark` suffix
 * convention. Keeps the inline anti-flash script and the store in agreement
 * without duplicating the registry.
 */
export function schemeFromId(id: string): ColorScheme {
  return id.endsWith('-dark') || id === 'dark' ? 'dark' : 'light'
}

/**
 * Nearest theme of the opposite scheme, for the header quick-toggle.
 * `light`⇄`dark`, `slate`⇄`slate-dark`, `custom`⇄`custom-dark`. Falls back to
 * the plain light/dark pair when a preset has no sibling.
 */
export function counterpart(id: string): string {
  const toDark = schemeFromId(id) === 'light'
  if (id === 'light') return 'dark'
  if (id === 'dark') return 'light'

  const paired = toDark ? `${id}-dark` : id.replace(/-dark$/, '')
  if (isCustomTheme(paired)) return paired
  return findTheme(paired) ? paired : toDark ? 'dark' : 'light'
}
